import React from "react";
import { Component } from "react";
import { Form, InputGroup, Button } from 'react-bootstrap';
import { timestampBins } from "../../chartUtils";
import { Definition, Entity, Property } from '../../data/Definition';
import { IDimension, IItem } from "../../data/Query";
import { isEntity, isProperty, isTimestampProperty } from "../../data/utils";

interface IDimensionCtrlProps {
  title:string;
  definition: Definition;
  config: IDimension | null;
  entities: Entity[];
  propertyTypes: string[];
  includeRoot: boolean;
  onUpdate: Function;
}

interface IMeasureCtrlState {
    showConfig : boolean,
    showTimeBinsConfig: boolean,
    isConfigValid: boolean,
    config: IDimension,
    current: IDimension | null
};

class DimensionCtrl extends Component<IDimensionCtrlProps, IMeasureCtrlState>  {
    private options: IItem[];

    static defaultProps = {
        title: 'Dimension (group by)',
        entities: [],
        propertyTypes: ['string', 'timestamp', 'integer', 'property_lookup'],
        includeRoot: false
      }

    constructor(props: any) {
        super(props);

        this.state = {
            showConfig: false,
            showTimeBinsConfig: false,
            isConfigValid: false,
            config: {} as IDimension,
            current: this.props.config
        };

        this.options = [];
    }

    componentDidMount() {
        this.initPropertyLists();
    }

    componentDidUpdate(prevProps:any){
        if(prevProps.config !== this.props.config){
            this.setState({
                current: this.props.config
            });
        }
    }

    initPropertyLists() {
        // add either passed entites or all entity relationships on the definition)
        if(this.props.entities.length === 0) {
            this.props.definition.entity.forEach((entity: Entity) => {
                this.options.push({
                    item: entity,
                    sub: null
                });
            });
        } else {
            const items = this.props.entities.map((entity: Entity) => {
                return {
                    item: entity,
                    sub: null
                }
            });

            this.options.push(...items);
        }

        if(this.props.includeRoot) {
            this.options.push({
                item: this.props.definition as Entity,
                sub: null
            });
        }

        // add the property types.
        this.props.definition.getPropertiesOfType(this.props.propertyTypes).forEach((property: Property) => {
            this.options.push({
                item: property,
                sub: null
            });
        });
    }

    render() {
        return (
            <Form.Group controlId="xDimension">
                <Form.Label>{this.props.title}</Form.Label>
                { this.renderCurrent() }
                { this.renderEdit() }
            </Form.Group>
        );
    }

    renderCurrent() {
        const {current} = this.state;

        return (
            this.state.showConfig ? "" :
                <InputGroup className="mb-3">
                    <Form.Control
                        type="text"
                        value={current && current.source.item  ? this.getDisplay(current) : ''}
                        readOnly
                        placeholder="add a dimension"
                        onClick={this.showConfig.bind(this)}
                    />

                    {current && current.source.item ?
                    <InputGroup.Append>
                        <Button variant="outline-secondary" onClick={this.onClear.bind(this)}>X</Button>
                    </InputGroup.Append>
                    : ''
                    }
                </InputGroup>
        );
    }

    renderEdit() {
        const {config, showConfig, isConfigValid} = this.state;

        return(
            showConfig ?
                <div style={{backgroundColor: "silver", padding: "7px"}}>
                    <Form.Control as="select" name="xDimension"
                        onChange={this.onItemChange.bind(this)}
                        defaultValue={config.source.item ? this.options.map((e:IItem) => { return e.item!.name; }).indexOf(config.source.item.name) : "-1"}>
                        <option value="-1">Select dimension</option>
                        {this.options.map((item: any, i: number) => (
                            <option key={i} value={i}>{this.getItemDisplay(item)}</option>
                        ))}
                    </Form.Control>

                    {
                        // binning for bindable types, for now, just timestamp.
                        config.source.item && isTimestampProperty(config.source.item) ?
                        <Form.Group controlId="xDimension">
                            <Form.Control as="select" name="xDimensionTimeBin" className="mt-1"
                                onChange={this.onChangeTimeBin.bind(this)}
                                value={config.bin ? config.bin : "-1"}>
                                <option value="-1">Select bin</option>
                                {timestampBins.map((item: any, i: number) => (
                                    <option key={item.name} value={item.value}>{item.name}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        :''
                    }

                    <div style={{paddingTop: "7px"}}>
                        <Button variant="warning" disabled={!isConfigValid} onClick={this.onSave.bind(this)}>Save</Button>
                        <Button variant="link" onClick={this.onCancel.bind(this)}>cancel</Button>
                    </div>
                </div>
            : ''
        );
    }

    isConfigValid(dimension: IDimension | null): boolean {
        let isItemValid: boolean = false;
        let isBinValid: boolean = false;

        if(dimension != null && dimension.source.item != null) {
            isItemValid = true;
            isBinValid = isTimestampProperty(dimension.source.item) ? dimension.bin != null && dimension.bin !== "-1" : true;
        }

        console.log('isConfigValid', isItemValid && isBinValid);

        return isItemValid && isBinValid;
    }

    onItemChange(e: any) {
        const dimension: IDimension = {
            source: {
                item: e.target.value === "-1" ? null : this.options[e.target.value].item,
                sub: null
            },
            bin: null
        };

        this.setState({
            isConfigValid: this.isConfigValid(dimension),
            config: dimension
        });
    }

    onChangeTimeBin(e: any) {
        const dimension: IDimension = {
            source: {
                item: this.state.config.source.item,
                sub: null
            },
            bin: e.target.value
        };

        this.setState({
            isConfigValid: this.isConfigValid(dimension),
            config: dimension
        });
    }

    onClear(e: any) {
        this.setState({
            current: null
        });
    }

    showConfig(e: any) {

        // copy state to config  
        const dimension: IDimension = this.state.current ? 
            { ...this.state.current, source: {...this.state.current.source}} : 
            { source: { item: null, sub: null }, bin: null};
    
        this.setState({
            showConfig: true,
            isConfigValid: this.isConfigValid(dimension),
            config: dimension
        });
    }

    onCancel(e: any) {
        this.setState({
            config: {
                source: {
                    item: null,
                    sub: null
                },
                bin: null
            }
        });

        this.setState({
            showConfig: false
        });
    }

    onSave(e: any) {
        const dimension: IDimension = this.state.config ? { ...this.state.config, source: {...this.state.config.source}} : { source: { item: null, sub: null }, bin: null};

        this.setState({
            showConfig: false,
            current: dimension
        }, () => {
            this.props.onUpdate(this.state.current);
        });
    }

    getDisplay(dimension: IDimension): string {
        let itemStr = this.getItemDisplay(dimension.source);
        let binStr = dimension.bin != null ? `bin:${dimension.bin}` : '';

        return `${itemStr} ${binStr}`;
    }

    getItemDisplay(item: IItem): string {
        if(isEntity(item.item)) {
            const entity = item.item as Entity;
            return `entity: ${entity.name}`;
        } else if(isProperty(item.item)) {
            const property = item.item as Property;
            return `property (${property.type}): ${property.name}`;
        }

        return '';
    }
}

export default DimensionCtrl;
